import React, { useCallback } from 'react'
import { v4 as uuid } from 'uuid'

import useAmplitudeTracking from '@/hooks/useAmplitudeTracking'
import { CONTINUE_WITH_GOOGLE } from '@/constants/amplitudeEvents'
import { GOOGLE_OAUTH_NONCE_KEY } from '@/lib/googleOAuthState'

type GoogleLoginProps = {
  invitationToken?: string
  disabled?: boolean
  onBeforeLogin?: () => void
}

const GoogleLoginButton: React.FC<GoogleLoginProps> = ({
  invitationToken,
  disabled,
  onBeforeLogin,
}) => {
  const { trackButtonClick } = useAmplitudeTracking()

  const login = useCallback(() => {
    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID
    const baseUrl = process.env.NEXT_PUBLIC_URL
    const redirectUrl = `${baseUrl}/users/oauth/google`
    // The callback only exchanges a code whose state carries the nonce this tab stored.
    const nonce = uuid()
    sessionStorage.setItem(GOOGLE_OAUTH_NONCE_KEY, nonce)
    const state = new URLSearchParams({ nonce })
    if (invitationToken) state.set('invitation_token', invitationToken)
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUrl}&response_type=code&scope=email%20profile%20openid&access_type=offline&state=${encodeURIComponent(
      state.toString()
    )}`
    window.location.href = url
  }, [invitationToken])

  return (
    <div className={'w-full'}>
      <button
        type={'button'}
        disabled={disabled}
        onClick={() => {
          onBeforeLogin?.()
          trackButtonClick(CONTINUE_WITH_GOOGLE)
          login()
        }}
        className="flex w-full h-10 items-center justify-center gap-3 rounded-md border border-line-neutral px-3 py-[7px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#24292F] disabled:cursor-not-allowed disabled:bg-interaction-disabled disabled:text-label-disabled"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="20px" height="20px">
          <path
            fill="#FFC107"
            d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
          ></path>
          <path
            fill="#FF3D00"
            d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
          ></path>
          <path
            fill="#4CAF50"
            d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
          ></path>
          <path
            fill="#1976D2"
            d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
          ></path>
        </svg>{' '}
        <span className="text-[15px] font-medium leading-6 text-label-neutral">
          Continue with Google
        </span>
      </button>
    </div>
  )
}

export default GoogleLoginButton
